import {
  generateDynamicColors,
  generateLetterSpacings,
  generateLineHeights,
  generateSpacingUnit,
  mapBreakpointsToString,
} from "./helpers";
import type {
  BreakpointConditions,
  Breakpoints,
  BreakpointsRange,
  CoreBorderRadius,
  CoreColor,
  DensityType,
  DynamicColor,
  ThemeTypographyBase,
  ThemeTypographyTags,
  Typography,
  Unit,
} from "./theme";

/** Breakpoints */
export const breakpoints: Breakpoints = {
  default: 0,
  over590: 590,
  over1024: 1024,
  over1236: 1236,
};

/** Breakpoints */
export const formGridBreakpoint: BreakpointsRange = {
  small: [0, 479],
  medium: [480, 767],
  large: [767],
};

export const breakpointConditions: BreakpointConditions =
  mapBreakpointsToString(breakpoints);

/**
 *
 * TYPOGRAPHY
 *
 */

export const fontWeights = {
  h1: "600",
  h2: "500",
  h3: "500",
  h4: "500",
  body: "400",
  caption: "400",
  button: "500",
};

export const fontSizes = {
  h1: "2rem",
  h2: "1.5rem",
  h3: "1.125rem",
  h4: "1rem",
  body: "1rem",
  caption: "0.875rem",
  button: "1rem",
};

export const lineHeights = generateLineHeights(fontSizes);
export const letterSpacings = generateLetterSpacings(fontSizes);

export const fontFamilies = {
  h1: "system-ui",
  h2: "system-ui",
  h3: "system-ui",
  h4: "system-ui",
  body: "system-ui",
  caption: "system-ui",
  button: "system-ui",
};

export const typographyBase: ThemeTypographyBase = {
  fontStretch: "normal",
  fontStyle: "normal",
  textDecoration: "none",
};

export const typographyTags: ThemeTypographyTags = {
  heading1: "h1",
  heading2: "h2",
  heading3: "h3",
  heading4: "h4",
  body: "p",
  caption: "span",
  button: "button",
};

export const typography: Typography = {
  heading1: {
    text: {
      default: "h2",
      over590: "h2",
      over1024: "h1",
      over1236: "h1",
    },
  },
  heading2: {
    text: {
      default: "h3",
      over590: "h3",
      over1024: "h2",
      over1236: "h2",
    },
  },
  heading3: {
    text: {
      default: "h4",
      over590: "h4",
      over1024: "h3",
      over1236: "h3",
    },
  },
  heading4: {
    text: {
      default: "h4",
      over590: "h4",
      over1024: "h4",
      over1236: "h4",
    },
  },
  body: {
    text: {
      default: "body",
      over590: "body",
      over1024: "body",
      over1236: "body",
    },
  },
  caption: {
    text: {
      default: "caption",
      over590: "caption",
      over1024: "caption",
      over1236: "caption",
    },
  },
  button: {
    text: {
      default: "button",
      over590: "button",
      over1024: "button",
      over1236: "button",
    },
  },
};

/**
 *
 * Z Index
 *
 */
const zIndex = {
  hidden: "-1", // remove from view
  body: "0", //base level
  overBody: "1000", // for dropdown menus and tooltips on body areas
  navigation: "2000", // the top navigation area
  overNavigation: "2500", // for dropdown menus and tooltips on naviagation areas
  alert: "3000", // for alert banners, toasts, etc
  modalScrim: "3999", // covers the whole screen for scrim below modal
  modal: "4000", // modal
  overModal: "4500", // for dropdown menus and tooltips on modal
  critical: "10000", // visible no matter what, on top of everything
};

/**
 * Spacing
 */

export const spacingBaseUnit = 4;
// This density value is used in documentation, in order to check unit value, please check generateSpacingUnit
export const density = {
  compact: 0.75,
  default: 1,
  spacious: 1.25,
};
export const densityType: DensityType = "default";
export const unit: Unit = generateSpacingUnit(densityType);

/**
 * Disabled Effect
 */
export const opacity = "0.4";

/**
 * Core BorderRadius
 */

export const coreBorderRadius: CoreBorderRadius = {
  form: 4,
  button: 4,
};

/**
 * Shared theme values
 */
export const sharedThemeValues = {
  opacity,
  border: {
    thin: "1px",
    medium: "1.5px", // This is the default stroke width for icons
  },
  borderRadius: {
    form: `${coreBorderRadius.form}px`,
    button: `${coreBorderRadius.button}px`,
  },
  breakpoints: breakpointConditions,
  outline: {
    standard: "2px",
    offset: "4px",
  },
  densityType,
  unit,
  typography: {
    fontSize: "16px",
    fontFamily: "system-ui",
    base: typographyBase,
    tags: typographyTags,
    variants: typography,
  },
  fontSizes,
  lineHeights,
  letterSpacings,
  fontWeights,
  fontFamilies,
  zIndex,
};

/**
 *
 * COLORS
 *
 */

/**
 * Core Colors
 */
export const coreColors: CoreColor = {
  brand: "hsla(221, 83%, 53%, 1)", // For action button backgrounds - use onBrand (dynamic token) as text color
  interactive: "hsla(221, 83%, 53%, 1)", // for action links
  pageBackground: "hsla(210, 40%, 98%, 1)",
  containerBackground: "hsla(0, 0%, 100%, 1)",
  formBackground: "hsla(0, 0%, 100%, 1)",
  primaryText: "hsla(217, 33%, 17%, 1)", // Primary text on pageBackground/containerBackground/formBackground
  secondaryText: "hsla(215, 16%, 48%, 1)", // Secondary text on pageBackground/containerBackground/formBackground
  tertiaryText: "hsla(214, 20%, 73%, 1)",
  border: "hsla(214, 21%, 60%, 1)",
  divider: "hsla(214, 32%, 91%, 1)",
  critical: "hsla(345, 83%, 41%, 1)",
  positive: "hsla(163, 94%, 24%, 1)",
  white: "hsla(0, 0%, 100%, 1)",
  black: "hsla(0, 0%, 0%, 1)",
  info: "hsla(218, 64%, 23%, 1)",
};

/**
 * Dynamic Colors
 */

export const dynamicColors: DynamicColor = generateDynamicColors(coreColors);
